import { useState, useEffect } from 'react';
import JsPDF from "jspdf";
import moment from "moment";
import html2canvas from "html2canvas";
import axios from "axios";
import { styled } from '@mui/material/styles';
import oculusIcon from '../assets/images/new/oculusnew.svg'
import vrIcon from '../assets/images/new/vrnew.svg'
import mobileIcon from '../assets/images/new/mobilenew.svg'
import desktopIcon from '../assets/images/new/desktopnew.svg'
import webglIcon from '../assets/images/new/webglnew.svg'

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export const humanReadableMinutes = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  return minutes;
};

export const humanReadable = (seconds) => {
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  seconds = Math.floor(seconds % 60);
  const parts = [
    { label: 'Days', value: days },
    { label: 'Hrs', value: hours },
    { label: 'Mins', value: minutes },
    { label: 'Secs', value: seconds },
  ];
  const nonZeroParts = parts.filter(part => part.value > 0);
  const result = nonZeroParts.slice(0, 2).map(part => `${part.value} ${part.label}`).join(' ');
  return result || '0 Secs';
};

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export const labels = {
  "MONTHLY": ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  "QUARTERLY": ['Q1', 'Q2', 'Q3', 'Q4'],
  "YEARLY": ['Current Year'],
}

export const kpiTypes = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
}

export const monitoringOptions = {
  "CUSTOM": 'Custom',
  "TOTAL_TRAININGS": 'Total Trainings',
  "ACTIVE_USERS": 'Active Users',
  "CERTIFIED_USERS": 'Certified Users'
};

export const graphTypes = {
  "BAR": "Bar Chart",
  "MIXED": "Mixed Chart",
  "LINE": "Line Chart"
}

export const cumulativeArray = (inputArray) => {
  if (!Array.isArray(inputArray)) {
    return [];
  }

  const cumulativeResult = [];
  let cumulativeSum = 0;

  for (let i = 0; i < inputArray.length; i++) {
    cumulativeSum += inputArray[i];
    cumulativeResult.push(cumulativeSum);
  }

  return cumulativeResult;
};

export const convertValuesToTarget = (array, targetType, inputType) => {
  if (inputType === kpiTypes.MONTHLY) {
    if (targetType === kpiTypes.MONTHLY) {
      // Month to Month (no change)
      return [...array];
    } else if (targetType === kpiTypes.QUARTERLY) {
      // Month to Quarter
      const result = [];
      for (let i = 0; i < 12; i += 3) {
        result.push(array.slice(i, i + 3).reduce((acc, val) => acc + val, 0));
      }
      return result;
    } else if (targetType === kpiTypes.YEARLY) {
      // Month to Year
      const result = [array.reduce((acc, val) => acc + val, 0)];
      return result;
    }
  } else if (inputType === kpiTypes.QUARTERLY) {
    if (targetType === kpiTypes.MONTHLY) {
      // Quarter to Month
      const result = [];
      for (let i = 0; i < 4; i++) {
        const quarterValue = array[i];
        for (let j = 0; j < 3; j++) {
          result.push(quarterValue / 3);
        }
      }
      return result;
    } else if (targetType === kpiTypes.QUARTERLY) {
      // Quarter to Quarter (no change)
      return [...array];
    } else if (targetType === kpiTypes.YEARLY) {
      // Quarter to Year
      const result = [array.reduce((acc, val) => acc + val, 0)];
      return result;
    }
  } else if (inputType === kpiTypes.YEARLY) {
    if (targetType === kpiTypes.MONTHLY) {
      // Year to Month
      const result = Array.from({ length: 12 }, () => array[0] / 12);
      return result;
    } else if (targetType === kpiTypes.QUARTERLY) {
      // Year to Quarter
      const result = Array.from({ length: 4 }, () => array[0] / 4);
      return result;
    } else if (targetType === kpiTypes.YEARLY) {
      // Year to Year (no change)
      return [...array];
    }
  }

  // Handle other input and target types if needed
  return [];
}

export const convertSecToMinute = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return formattedTime;
}

export const platformList = {
  "Desktop": "MODULE.ASSET.DESKTOP",
  "Desktop VR": "MODULE.ASSET.DESKTOP.VR",
  "Mobile": "MODULE.ASSET.MOBILE",
  "Standalone VR": "MODULE.ASSET.STANDALONE.VR",
  "WebGL": "MODULE.ASSET.WEB"
}
export const platformIcon = {
  "Online": webglIcon,
  "Desktop": desktopIcon,
  "Desktop VR": vrIcon,
  "Mobile": mobileIcon,
  "Standalone VR": oculusIcon,
}

export const staticFilter = {
  "Online": "MODULE.ASSET.WEB",
  "Free": "Free"
}

export const getUserCertificate = async (UNO_URL, token, MODE) => {
  let { data } = await axios.get(`${UNO_URL}/api/application/certificate/configuration/${MODE}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });
  return data.data;
}

export const certificateDownload = async ({ certificate, moduleName, certificateData, userData = {}, outcomesData, courseDuration, certificateConfig }) => {
  try {
    const base64ImageOrgLogo = await fetch(certificateConfig.orgLogo)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }));

    const base64ImageSignature = await fetch(certificateConfig.signature)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }));

    const base64SimulanisLogo = await fetch(certificateData.unoLogo)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }));

    const base64ImageProductLogo = await fetch(certificateData.logo)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }));


    let issuedDate = moment(certificate.createdAt).format("LL");
    const response = await axios.get(certificateData.certificateConfig.certificateLink);
    var htmlContent = response.data;
    htmlContent = htmlContent.replace("{{USER_NAME}}", userData.name);
    htmlContent = htmlContent.replace("{{MODULE_NAME}}", moduleName);
    htmlContent = htmlContent.replace("{{ISSUED_DATE}}", issuedDate);

    if (certificateData.unoLogo) {
      htmlContent = htmlContent.replace("{{ PARENT_LOGO }}", `data:image/png;base64,${base64SimulanisLogo.split(',')[1]}`);
    }

    if (certificateData.logo) {
      htmlContent = htmlContent.replace("{{ PRODUCT_LOGO }}", `data:image/png;base64,${base64ImageProductLogo.split(',')[1]}`);
    }


    if (certificateConfig.orgName) {
      htmlContent = htmlContent.replace("{{ ORGANIZATION_NAME }}", certificateConfig.orgName);
    } else { htmlContent = htmlContent.replace("{{ ORGANIZATION_NAME }}", ""); }

    if (certificateConfig.orgLogo) {
      htmlContent = htmlContent.replace("{{ ORG_LOGO }}", `data:image/png;base64,${base64ImageOrgLogo.split(',')[1]}`);
    } else { htmlContent = htmlContent.replace("{{ ORG_LOGO }}", ""); }

    if (certificateConfig.signature) {
      htmlContent = htmlContent.replace("{{ SIGNATURE }}", `data:image/png;base64,${base64ImageSignature.split(',')[1]}`);
    } else { htmlContent = htmlContent.replace("{{ SIGNATURE }}", ""); }

    if (certificateConfig.signatory) {
      htmlContent = htmlContent.replace("{{ SIGNATORY }}", certificateConfig.signatory);
    } else { htmlContent = htmlContent.replace("{{ SIGNATORY }}", ""); }

    htmlContent = htmlContent.replace("{{ DESIGNATION }}", certificateConfig.designation || "")

    let outcomes = "";

    if (outcomesData) {
      for (let i = 0; i < outcomesData.length; i++) {
        outcomes += `<li><span></span>${outcomesData[i]}</li>`;
      }
    }

    htmlContent = htmlContent.replace("{{OUTCOMES}}", outcomes);
    htmlContent = htmlContent.replace("[UUID]", certificate.uuid);
    htmlContent = htmlContent.replace("[COURSE_DURATION]", courseDuration);
    let iframe = document.createElement("iframe");
    document.body.appendChild(iframe);
    let iframedoc = iframe.contentDocument || iframe.contentWindow.document;
    iframedoc.body.innerHTML = htmlContent;
    let canvas = await html2canvas(iframedoc.body, {
      scale: 2,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: certificateData.certificateConfig.htmlWidth,
      windowHeight: certificateData.certificateConfig.htmlHeight,
    });

    const htmlWidth = certificateData.certificateConfig.htmlWidth
    const htmlHeight = certificateData.certificateConfig.htmlHeight;
    const pdfWidth = 210; // A4 width in mm
    const pdfHeight = 297; // A4 height in mm
    const scaleX = pdfWidth / htmlWidth;
    const scaleY = pdfHeight / htmlHeight;
    const scale = Math.min(scaleX, scaleY);
    const imgWidth = htmlWidth * scale;
    const imgHeight = htmlHeight * scale;
    let imgData = canvas.toDataURL("image/png");
    const doc = new JsPDF({
      format: [imgWidth, imgHeight],
      orientation: certificateData.certificateConfig.orientation,
      unit: "mm",
      marginLeft: 0,
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
    });

    doc.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    doc.save(`${moduleName} certificate.pdf`);
    window.open(doc.output("bloburl"));
    document.body.removeChild(iframe);
  } catch (error) {
    console.log(error);
  } finally {
    return
  }
};

export const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
  },
}));

export const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#3e4095',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#3e4095',
  },
});